import React from 'react';
import { 
  Network, 
  Shield, 
  Server, 
  Cable,
  Wifi,
  HardDrive,
  Lock,
  Activity,
  Settings,
  Database,
  Cloud,
  MonitorSmartphone
} from 'lucide-react';

const ServicesGrid = () => {
  const services = [
    {
      icon: Network,
      title: 'Network Infrastructure',
      description: 'Design, implementation, and management of robust network infrastructure including LAN, WAN, and wireless solutions.',
      image: '/images/services/network1.png', // You'll add this image
      features: [
        'Network Design & Architecture',
        'Rack Installation & Cable Management',
        'Switch & Router Configuration',
        'Network Performance Optimization',
        'Structured Cabling Solutions',
        'Fiber Optic Installation'
      ],
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      icon: Shield,
      title: 'Cybersecurity Services',
      description: 'Comprehensive security solutions to protect your business from cyber threats and ensure regulatory compliance.',
      image: '/images/services/cybersecurity.jpeg',
      features: [
        'Security Assessment & Audits',
        'Firewall Configuration',
        'Intrusion Detection Systems',
        'Vulnerability Management',
        'Security Policy Development',
        'Incident Response Planning'
      ],
      color: 'text-red-500',
      bgColor: 'bg-red-50',
    },
    {
      icon: Server,
      title: 'Server Infrastructure',
      description: 'Enterprise-grade server solutions for optimal performance, reliability, and scalability.',
      image: '/images/services/infra.jpg',
      features: [
        'Server Installation & Configuration',
        'Virtualization Solutions',
        'Storage Area Networks (SAN)',
        'Backup & Disaster Recovery',
        'Server Monitoring & Maintenance',
        'High Availability Clustering'
      ],
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
    },
    {
      icon: Wifi,
      title: 'Wireless Solutions',
      description: 'Advanced wireless networking solutions for seamless connectivity across your organization.',
      image: '/images/services/wireless.jpg',
      features: [
        'WiFi Site Surveys',
        'Enterprise Access Points',
        'Wireless Security',
        'Guest Network Setup',
        'Coverage Optimization',
        'WiFi 6/6E Deployment'
      ],
      color: 'text-green-500',
      bgColor: 'bg-green-50',
    },
    {
      icon: Cloud,
      title: 'Cloud Solutions',
      description: 'Migrate and manage your infrastructure in the cloud for improved flexibility and cost-efficiency.',
      image: '/images/services/enterprise-software.png',
      features: [
        'Cloud Migration Strategy',
        'AWS & Azure Services',
        'Cloud Security',
        'Hybrid Cloud Solutions',
        'Cloud Cost Optimization',
        'Multi-Cloud Management'
      ],
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-50',
    },
    {
      icon: Activity,
      title: 'Network Monitoring',
      description: '24/7 network monitoring and management to ensure optimal performance and quick issue resolution.',
      image: '/images/services/network-monitoringjpg.jpg',
      features: [
        'Real-time Network Monitoring',
        'Performance Analytics',
        'Automated Alerting',
        'Network Traffic Analysis',
        'Capacity Planning',
        'SLA Management'
      ],
      color: 'text-orange-500',
      bgColor: 'bg-orange-50',
    },
  ];

  return (
    <section className="section-padding bg-background">
      <div className="max-w-7xl mx-auto container-padding">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            What We <span className="text-gradient">Offer</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Professional IT services tailored to meet the unique needs of your business
          </p>
        </div>

        {/* Services Grid */}
        <div className="space-y-16">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            const isEven = index % 2 === 0;
            
            return (
              <div 
                key={service.title}
                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center animate-slide-up`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Image Side */}
                <div className="w-full lg:w-1/2">
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-tech opacity-20 rounded-2xl group-hover:opacity-30 transition-opacity"></div>
                    <div className="relative h-80 bg-secondary-100 rounded-2xl overflow-hidden shadow-elevation-2">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>

                {/* Content Side */}
                <div className="w-full lg:w-1/2">
                  <div className="bg-surface rounded-2xl p-8 shadow-elevation-1 hover:shadow-elevation-3 transition-shadow">
                    <div className={`w-14 h-14 ${service.bgColor} rounded-xl flex items-center justify-center mb-6`}>
                      <IconComponent className={`w-7 h-7 ${service.color}`} />
                    </div>
                    
                    <h3 className="text-2xl md:text-3xl font-bold text-text-primary mb-4">
                      {service.title}
                    </h3>
                    
                    <p className="text-text-secondary mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    {/* Features List */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {service.features.map((feature) => (
                        <div key={feature} className="flex items-start">
                          <div className={`w-2 h-2 ${service.color.replace('text-', 'bg-')} rounded-full mt-2 mr-3 flex-shrink-0`}></div>
                          <span className="text-sm text-text-secondary">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <button className="mt-6 btn-ghost text-primary hover:bg-primary-50">
                      Learn More →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
